/**
 * Collection-only table of contents behavior.
 * Loaded only on collection templates by the theme layouts.
 */
// =============================================================================
// Collection Table of Contents (TOC) Generator
// =============================================================================

/**
 * Dynamically generates a Table of Contents from H2 headings on collection pages
 * Features:
 * - Auto-generates anchor IDs from headings
 * - Smooth scroll to sections
 * - Collapsible on mobile
 * - Active section highlighting on scroll
 */
const CollectionTOC = {
  config: {
    contentSelector: '.html.rte, .rte, .collection__description, .page-content, .main-content, [data-section-type="html"]',
    headingSelector: 'h2',
    tocContainerId: 'collection-toc',
    tocInsertAfter: '.product-list, .collection__products, .collection__dynamic-part',
    minHeadings: 2, // Minimum headings required to show TOC
    mobileBreakpoint: 768,
    scrollOffset: 105, // Offset when CLICKING TOC links (matches header height)
    scrollSpyOffset: 150, // Offset for scroll spy detection as you scroll
    activeClass: 'toc-active',
    collapsedClass: 'toc-collapsed'
  },

  headings: [],
  tocElement: null,
  observer: null,

  /**
   * Compute responsive offsets for click and scroll spy
   */
  getResponsiveOffsets: function() {
    const w = window.innerWidth || document.documentElement.clientWidth;
    // Mobile
    if (w < 768) {
      return { scrollOffset: 440, scrollSpyOffset: 105 };
    }
    // Tablet
    if (w < 1024) {
      return { scrollOffset: 85, scrollSpyOffset: 130 };
    }
    // Desktop
    return { scrollOffset: 125, scrollSpyOffset: 125 };
  },

  /**
   * Apply responsive offsets and refresh observers/attributes
   */
  applyResponsiveOffsets: function() {
    const responsive = this.getResponsiveOffsets();
    this.config.scrollOffset = responsive.scrollOffset;
    this.config.scrollSpyOffset = responsive.scrollSpyOffset;

    // Ensure TOC link anchors carry the offset for the theme anchor handler
    if (this.tocElement) {
      const links = this.tocElement.querySelectorAll('.toc-link');
      links.forEach((a) => a.setAttribute('data-offset', String(this.config.scrollOffset)));
    }

    // Recreate scroll spy observer with new root margins
    if (this.observer) {
      try { this.observer.disconnect(); } catch(e) {}
      this.observer = null;
    }
    if (this.headings && this.headings.length) {
      this.setupScrollSpy();
    }
  },

  /**
   * Initialize TOC generator and inject styles
   */
  init: function() {
    // Only run on collection pages
    if (!document.body.classList.contains('template-collection')) {
      return;
    }

    // Inject CSS styles
    this.injectStyles();

    // Wait for content to load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Apply responsive offsets first so generate uses them
        this.applyResponsiveOffsets();
        this.generate();
        // Apply once more post-generate to update anchors and observer
        this.applyResponsiveOffsets();
      });
    } else {
      // Apply responsive offsets first so generate uses them
      this.applyResponsiveOffsets();
      this.generate();
      // Apply once more post-generate to update anchors and observer
      this.applyResponsiveOffsets();
    }

    // Keep offsets in sync with viewport changes
    let _tocResizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(_tocResizeTimer);
      _tocResizeTimer = setTimeout(() => this.applyResponsiveOffsets(), 200);
    });
  },

  /**
   * Inject CSS styles for TOC and Jump Link
   */
  injectStyles: function() {
    if (document.getElementById('collection-toc-styles')) {
      return; // Already injected
    }

    const style = document.createElement('style');
    style.id = 'collection-toc-styles';
    style.textContent = `
      /* ===== Collection Jump Link Styles ===== */
      .collection-jump-link {
        background: transparent;
      }
      .jump-link-container{
        margin-top: 10px;
      }

      .jump-link-text {
        margin: 0;
        font-size: 13px;
        line-height: 1.6;
        color: #6D6D6D;
      }

      .jump-link-link {
        color: #28427a;
        text-decoration: underline;
        font-weight: 700;
        transition: color 0.2s ease;
      }

      .jump-link-link:hover {
        color: #1e3a68;
        text-decoration: underline;
      }


      /* ===== TOC + Content Wrapper for Sidebar Layout ===== */
      .toc-content-wrapper {
        display: flex;
        gap: 40px;
        align-items: flex-start;
        max-width: 1400px;
        margin: 40px auto;
        padding: 0 20px;
      }

      .toc-content-area {
        flex: 1;
        min-width: 0;
      }

      /* ===== Table of Contents Styles (Sticky Sidebar) ===== */
      .collection-toc {
        flex: 0 0 280px;
        position: sticky;
        top: 170px;
        max-height: calc(100vh - 190px);
        background: transparent;
        margin: 0;
        padding: 0;
        overflow: hidden;
      }

      .toc-container {
        width: 100%;
      }

      .toc-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        background: transparent;
        cursor: default;
      }

      .toc-title {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: #000;
      }

      .toc-toggle {
        background: none;
        border: none;
        padding: 6px;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        color: #64748b;
      }

      .toc-toggle:hover {
        background: #f1f5f9;
        color: #000;
      }

      .collection-toc.toc-collapsed .toc-toggle-icon {
        transform: rotate(-90deg);
      }

      .toc-nav {
        max-height: calc(100vh - 250px);
        overflow-y: auto;
        opacity: 1;
      }

      .collection-toc.toc-collapsed .toc-nav {
        max-height: 0;
        opacity: 0;
        overflow: hidden;
      }

      .toc-list {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .toc-item {
        border-bottom: none;
      }

      .toc-item:last-child {
        border-bottom: none;
      }

      .toc-link {
        display: block;
        padding: 12px 20px;
        text-decoration: none;
        color: #6d6d6d;
        background: transparent;
        font-size: 14px;
        line-height: 1.5;
        font-weight: 600;
      }

      .toc-link:hover {
        background: transparent;
        color: #6d6d6d;
      }

      .toc-link.toc-active {
        background: transparent;
        color: #28427a;
        font-weight: 600;
        border-left: 3px solid #28427a;
        padding-left: 17px;
      }

      .toc-number {
        font-weight: 700;
        color: #6d6d6d;
        font-size: 13px;
        min-width: 24px;
        flex-shrink: 0;
      }

      .toc-link.toc-active .toc-number {
        color: #28427a;
      }

      .toc-text {
        flex: 1;
        line-height: 1.4;
      }

      /* Minimalistic scrollbar for TOC */
      .toc-nav::-webkit-scrollbar {
        width: 4px;
      }

      .toc-nav::-webkit-scrollbar-track {
        background: transparent;
      }

      .toc-nav::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
      }

      .toc-nav::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }

      /* Mobile Styles - Stack Layout */
      @media (max-width: 768px) {
        .toc-content-wrapper {
          flex-direction: column;
          gap: 24px;
          margin: 24px auto;
        }

        .collection-toc {
          position: relative;
          top: auto;
          max-height: none;
          width: 100%;
          flex: 1 1 auto;
        }

        .toc-header {
          padding: 14px 16px;
          cursor: pointer;
        }

        .toc-title {
          font-size: 15px;
        }

        .toc-toggle {
          display: flex;
        }

        .toc-link {
          padding: 10px 16px;
          font-size: 13px;
        }

        .toc-link.toc-active {
          padding-left: 17px;
        }

        .collection-toc.toc-collapsed .toc-nav {
          max-height: 0;
        }
      }

      /* Desktop - always show TOC, hide toggle */
      @media (min-width: 769px) {
        .toc-toggle {
          display: none !important;
        }
      }

      /* Print styles */
      @media print {
        .collection-toc {
          break-inside: avoid;
          border: 1px solid #000;
          position: relative;
          top: auto;
        }

        .toc-toggle {
          display: none;
        }

        .collection-toc.toc-collapsed .toc-nav {
          max-height: none;
          opacity: 1;
        }

        .toc-content-wrapper {
          display: block;
        }
      }
    `;

    document.head.appendChild(style);
  },

  /**
   * Generate the Table of Contents
   */
  generate: function() {
    // Find all content areas (including custom HTML sections)
    const contentAreas = document.querySelectorAll(this.config.contentSelector);
    if (contentAreas.length === 0) {
      console.log('TOC: Content area not found');
      return;
    }

    // Collect all H2 headings from all content areas
    this.headings = [];
    contentAreas.forEach(area => {
      const areaHeadings = Array.from(area.querySelectorAll(this.config.headingSelector));
      this.headings = this.headings.concat(areaHeadings);
    });

    // Filter out headings in excluded areas (product cards, navigation, etc.)
    this.headings = this.headings.filter(heading => {
      return !heading.closest('.toc-exclude, .product-item, .collection-block, nav, header, footer, .mosaic');
    });

    // Remove duplicate headings (in case they were selected from multiple areas)
    this.headings = [...new Set(this.headings)];

    if (this.headings.length < this.config.minHeadings) {
      console.log(`TOC: Not enough headings found (found ${this.headings.length}, need ${this.config.minHeadings})`);
      return;
    }

    // Generate unique IDs for headings
    this.headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = this.slugify(heading.textContent) || `section-${index + 1}`;
      }
    });

    // Create TOC HTML
    this.createTOC();

    // Set up scroll spy
    this.setupScrollSpy();

    // Set up mobile collapse
    this.setupMobileCollapse();

    console.log(`TOC: Generated with ${this.headings.length} sections`);
  },

  /**
   * Create TOC HTML structure
   */
  createTOC: function() {
    // Check if TOC already exists
    if (document.getElementById(this.config.tocContainerId)) {
      return;
    }

    // Create TOC container
    const toc = document.createElement('div');
    toc.id = this.config.tocContainerId;
    toc.className = 'collection-toc';

    // Add mobile breakpoint class
    if (window.innerWidth < this.config.mobileBreakpoint) {
      toc.classList.add(this.config.collapsedClass);
    }

    // Build TOC HTML
    let tocHTML = `
      <div class="toc-container">
        <div class="toc-header">
          <h3 class="toc-title">
            Table of Contents
          </h3>
          <button class="toc-toggle" aria-label="Toggle table of contents">
            <svg class="toc-toggle-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <nav class="toc-nav" aria-label="Table of contents">
          <ul class="toc-list">
    `;

    // Add each heading as a link
    this.headings.forEach((heading, index) => {
      const text = heading.textContent.trim();
      const id = heading.id;
      tocHTML += `
        <li class="toc-item">
          <a href="#${id}" class="toc-link" data-section="${id}" data-offset="${this.config.scrollOffset}">
            <span class="toc-text">${text}</span>
          </a>
        </li>
      `;
    });

    tocHTML += `
          </ul>
        </nav>
      </div>
    `;

    toc.innerHTML = tocHTML;

    // Find the content area that contains the H2 headings
    let contentArea = null;
    for (const heading of this.headings) {
      const parent = heading.closest('.html.rte, .rte, [data-section-type="html"]');
      if (parent) {
        contentArea = parent;
        break;
      }
    }

    // Insert TOC with sidebar layout
    let insertPoint = null;
    const insertSelectors = this.config.tocInsertAfter.split(',').map(s => s.trim());

    for (const selector of insertSelectors) {
      insertPoint = document.querySelector(selector);
      if (insertPoint) break;
    }

    if (insertPoint && contentArea) {
      // Create wrapper for sidebar layout
      const wrapper = document.createElement('div');
      wrapper.className = 'toc-content-wrapper';

      // Wrap content area
      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'toc-content-area';

      // Move content into wrapper
      const contentParent = contentArea.parentNode;
      contentParent.insertBefore(wrapper, contentArea);
      contentWrapper.appendChild(contentArea);

      // Add TOC and content to wrapper
      wrapper.appendChild(toc);
      wrapper.appendChild(contentWrapper);

    } else if (insertPoint) {
      // Fallback: insert after product list without wrapper
      insertPoint.parentNode.insertBefore(toc, insertPoint.nextSibling);
    } else {
      console.log('TOC: Could not find insertion point');
      return;
    }

    this.tocElement = toc;

    // Add smooth scroll to links
    this.setupSmoothScroll();
  },

  /**
   * Set up smooth scrolling for TOC links
   */
  setupSmoothScroll: function() {
    const links = this.tocElement.querySelectorAll('.toc-link');

    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const targetId = link.getAttribute('data-section');
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          // Use document-based coordinates for consistent behavior across devices
          const rectTop = targetElement.getBoundingClientRect().top;
          const absoluteTop = (window.pageYOffset || document.documentElement.scrollTop || 0) + rectTop;
          const scrollTop = Math.max(0, absoluteTop - this.config.scrollOffset);
          // console.log('🎯 TOC Click - scrollOffset:', this.config.scrollOffset, 'Scrolling to:', scrollTop);

          window.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          // Update active state
          this.setActiveLink(link);

          // Collapse on mobile after click
          if (window.innerWidth < this.config.mobileBreakpoint) {
            this.tocElement.classList.add(this.config.collapsedClass);
          }
        }
      });
    });
  },

  /**
   * Set up scroll spy to highlight active section
   */
  setupScrollSpy: function() {
    const options = {
      root: null,
      rootMargin: `-${this.config.scrollSpyOffset}px 0px -85% 0px`,
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const link = this.tocElement.querySelector(`.toc-link[data-section="${id}"]`);
          if (link) {
            this.setActiveLink(link);
          }
        }
      });
    }, options);

    // Observe all headings
    this.headings.forEach(heading => {
      this.observer.observe(heading);
    });
  },

  /**
   * Set active link in TOC
   */
  setActiveLink: function(activeLink) {
    // Remove active class from all links
    const links = this.tocElement.querySelectorAll('.toc-link');
    links.forEach(link => link.classList.remove(this.config.activeClass));

    // Add active class to current link
    activeLink.classList.add(this.config.activeClass);
  },

  /**
   * Set up mobile collapse functionality
   */
  setupMobileCollapse: function() {
    const toggle = this.tocElement.querySelector('.toc-toggle');
    const header = this.tocElement.querySelector('.toc-header');

    if (toggle) {
      toggle.addEventListener('click', () => {
        this.tocElement.classList.toggle(this.config.collapsedClass);
      });
    }

    // Also allow clicking header to toggle
    if (header) {
      header.addEventListener('click', (e) => {
        if (e.target.closest('.toc-toggle')) return; // Don't double-toggle
        if (window.innerWidth < this.config.mobileBreakpoint) {
          this.tocElement.classList.toggle(this.config.collapsedClass);
        }
      });
    }

    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= this.config.mobileBreakpoint) {
          this.tocElement.classList.remove(this.config.collapsedClass);
        } else if (!this.tocElement.classList.contains(this.config.collapsedClass)) {
          this.tocElement.classList.add(this.config.collapsedClass);
        }
      }, 250);
    });
  },

  /**
   * Convert text to URL-friendly slug
   */
  slugify: function(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')     // Replace spaces with -
      .replace(/[^\w\-]+/g, '') // Remove non-word chars
      .replace(/\-\-+/g, '-')   // Replace multiple - with single -
      .replace(/^-+/, '')       // Trim - from start
      .replace(/-+$/, '');      // Trim - from end
  },

  /**
   * Destroy TOC and clean up
   */
  destroy: function() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.tocElement) {
      this.tocElement.remove();
    }
    this.headings = [];
  }
};

// Initialize Collection TOC
CollectionTOC.init();
